import ApiClient from "../client";
import {
  ApiCategory,
  ApiCompetitionCreateEntryWithProfilePayload,
  ApiCompetitionBulkAttemptPayload,
  ApiCompetitionBulkAttemptsResponse,
  ApiCompetitionCreateEntryPayload,
  ApiCompetitionDetail,
  ApiCompetitionEntryDetail,
  ApiCompetitionEntryInline,
  ApiCompetitionUpdateEntryPayload,
  ApiCreateCategoryPayload,
  ApiCreateCompetitionPayload,
  ApiUpdateCompetitionPayload,
  PaginatedResponse,
  ApiLeague,
  ApiCreateLeaguePayload,
  ApiLeagueEntryInline,
  ApiLeagueEntry,
  ApiStage,
  ApiCompetitionRouteCustomScoringConfig,
  ApiCompetitionLookupMatch,
  ApiSponsor,
} from "../types";

export const getCompetition = (client: ApiClient, competitionUuid: string) => {
  return client.get<ApiCompetitionDetail>(`/competitions/${competitionUuid}`);
};

export const getCompetitionEntries = (
  client: ApiClient,
  competitionUuid: string
) => {
  return client.get<ApiCompetitionEntryInline[]>(
    `/competitions/${competitionUuid}/entries`
  );
};

export const getCompetitionEntry = (
  client: ApiClient,
  competitionUuid: string,
  userUuid: string
) => {
  return client.get<ApiCompetitionEntryDetail>(
    `/competitions/${competitionUuid}/entries/${userUuid}`
  );
};

export const updateCompetitionEntry = (
  client: ApiClient,
  competitionUuid: string,
  userUuid: string,
  payload: ApiCompetitionUpdateEntryPayload
) => {
  return client.patch<ApiCompetitionEntryDetail>(
    `/competitions/${competitionUuid}/entries/${userUuid}`,
    payload
  );
};

export const deleteCompetitionEntry = (
  client: ApiClient,
  competitionUuid: string,
  userUuid: string
) => {
  return client.delete(`/competitions/${competitionUuid}/entries/${userUuid}`);
};

export const createCompetitionEntry = (
  client: ApiClient,
  competitionUuid: string,
  payload: ApiCompetitionCreateEntryPayload
) => {
  return client.post<ApiCompetitionEntryDetail>(
    `/competitions/${competitionUuid}/entries`,
    payload
  );
};

export const createCompetitionEntryWithProfile = (
  client: ApiClient,
  competitionUuid: string,
  payload: ApiCompetitionCreateEntryWithProfilePayload
) => {
  return client.post<ApiCompetitionEntryDetail>(
    `/competitions/${competitionUuid}/entries/create-with-profile`,
    payload
  );
};

export const createCompetition = (
  client: ApiClient,
  payload: ApiCreateCompetitionPayload
) => {
  return client.post<ApiCompetitionDetail>("/competitions", payload);
};

export const updateCompetition = (
  client: ApiClient,
  competitionUuid: string,
  payload: ApiUpdateCompetitionPayload
) => {
  return client.patch<ApiCompetitionDetail>(
    `/competitions/${competitionUuid}`,
    payload
  );
};

export const deleteCompetition = (
  client: ApiClient,
  competitionUuid: string
) => {
  return client.delete(`/competitions/${competitionUuid}`);
};

export const createCompetitionCategory = (
  client: ApiClient,
  payload: ApiCreateCategoryPayload
) => {
  return client.post<ApiCategory>(`/competitions/categories`, payload);
};

export const updateCompetitionCategory = (
  client: ApiClient,
  categoryUuid: string,
  payload: Partial<ApiCreateCategoryPayload>
) => {
  return client.patch<ApiCategory>(
    `/competitions/categories/${categoryUuid}`,
    payload
  );
};

export const deleteCompetitionCategory = (
  client: ApiClient,
  categoryUuid: string
) => {
  return client.delete(`/competitions/categories/${categoryUuid}`);
};

export const getAllCompetitionAttempts = (
  client: ApiClient,
  competitionUuid: string
) => {
  return client.get<ApiCompetitionBulkAttemptsResponse>(
    `/competitions/${competitionUuid}/attempts?attempts_only=1`
  );
};

export const getCompetitionAttempts = (
  client: ApiClient,
  competitionUuid: string,
  options?: { userUuids?: string[]; page?: number }
) => {
  const params = new URLSearchParams({ attempts_only: "1" });
  if (options?.userUuids?.length) {
    params.set("user_uuids", options.userUuids.join(","));
  } else if (options?.page !== undefined) {
    params.set("page", String(options.page));
  }
  return client.get<ApiCompetitionBulkAttemptsResponse>(
    `/competitions/${competitionUuid}/attempts?${params.toString()}`
  );
};

export const bulkUpdateAttempts = (
  client: ApiClient,
  competitionUuid: string,
  payload: ApiCompetitionBulkAttemptPayload
) => {
  return client.patch<ApiCompetitionBulkAttemptsResponse>(
    `/competitions/${competitionUuid}/attempts`,
    payload
  );
};

export const getCompetitionLeaderboardXlsx = (
  client: ApiClient,
  competitionUuid: string
) => {
  return client.get(`/competitions/${competitionUuid}/entries.xls`, {
    returnResponse: true,
  });
};

export const generateCompetitionLeaderboardPdf = (
  client: ApiClient,
  competitionUuid: string
) => {
  return client.post<{ url: string }>(
    `/competitions/${competitionUuid}/entries/generate-pdf`
  );
};

export const getLeague = (client: ApiClient, leagueUuid: string) => {
  return client.get<ApiLeague>(`/leagues/${leagueUuid}`);
};

export const createLeague = (
  client: ApiClient,
  payload: ApiCreateLeaguePayload
) => {
  return client.post<ApiLeague>(`/leagues`, payload);
};

export const updateLeague = (
  client: ApiClient,
  leagueUuid: string,
  payload: Partial<ApiCreateLeaguePayload>
) => {
  return client.patch<ApiLeague>(`/leagues/${leagueUuid}`, payload);
};

export const deleteLeague = (client: ApiClient, leagueUuid: string) => {
  return client.delete(`/leagues/${leagueUuid}`);
};

export const getLeagueLeaderboard = (client: ApiClient, leagueUuid: string) => {
  return client.get<ApiLeagueEntryInline[]>(
    `/leagues/${leagueUuid}/leaderboard`
  );
};

export const getLeagueEntry = (
  client: ApiClient,
  leagueUuid: string,
  userUuid: string
) => {
  return client.get<ApiLeagueEntry>(
    `/leagues/${leagueUuid}/leaderboard/${userUuid}`
  );
};

export const getLeagueLeaderboardXls = (
  client: ApiClient,
  leagueUuid: string
) => {
  return client.get(`/leagues/${leagueUuid}/leaderboard.xls`, {
    returnResponse: true,
  });
};

export const updateCompetitionStage = (
  client: ApiClient,
  competitionUuid: string,
  stageUuid: string,
  payload: Partial<{ pendingResults: boolean }>
) => {
  return client.patch<ApiStage>(
    `/competitions/${competitionUuid}/stages/${stageUuid}`,
    payload
  );
};

export const getCompetitionRouteCustomScoringConfigs = (
  client: ApiClient,
  competitionUuid: string
) => {
  return client.get<ApiCompetitionRouteCustomScoringConfig[]>(
    `/competitions/${competitionUuid}/route-custom-scoring-configs`
  );
};

export const getCompetitionUserLookupMatches = (
  client: ApiClient,
  competitionUuid: string,
  searchQuery: string
) => {
  return client.get<ApiCompetitionLookupMatch[]>(
    `/competitions/${competitionUuid}/user-lookup?q=${searchQuery}`
  );
};

export const mergeLeagueEntries = (
  client: ApiClient,
  leagueUuid: string,
  payload: {
    sourceUser: string;
    targetUser: string;
  }
) => {
  return client.post<void>(`/leagues/${leagueUuid}/merge-entries`, payload);
};

export const getSponsorsList = (client: ApiClient) => {
  return client.get<PaginatedResponse<ApiSponsor>>(`/competitions/sponsors`);
};

export const endpoints = {
  getCompetition,
  getCompetitionEntries,
  getCompetitionEntry,
  updateCompetitionEntry,
  deleteCompetitionEntry,
  createCompetitionEntry,
  createCompetitionEntryWithProfile,
  createCompetition,
  updateCompetition,
  deleteCompetition,
  createCompetitionCategory,
  updateCompetitionCategory,
  deleteCompetitionCategory,
  getAllCompetitionAttempts,
  getCompetitionAttempts,
  bulkUpdateAttempts,
  getCompetitionLeaderboardXlsx,
  generateCompetitionLeaderboardPdf,
  getLeague,
  createLeague,
  updateLeague,
  deleteLeague,
  getLeagueEntries: getLeagueLeaderboard,
  getLeagueEntry,
  getLeagueLeaderboardXls,
  updateCompetitionStage,
  getCompetitionRouteCustomScoringConfigs,
  getCompetitionUserLookupMatches,
  mergeLeagueEntries,
  getSponsorsList,
};
